import { Component, inject, viewChild, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ReceiptDatasourceService } from '../receipt-datasource.service';
import { CommonModule } from '@angular/common';
import { ReceiptGridComponent } from '../receipt-grid/receipt-grid.component';
import { ReceiptColumnConfigComponent } from '../receipt-column-config/receipt-column-config.component';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { BreadcrumbsComponent, PageContainerComponent } from 'wecheer-common';
import { ScreenService } from 'src/app/common/services/screen.service';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { CampaignsReceiptAdminApiClient, Receipt } from 'src/app/api/api-campaigns.service.generated';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-receipt-search',
  templateUrl: './receipt-search.component.html',
  styleUrls: ['./receipt-search.component.scss'],
  imports: [
    ReceiptGridComponent,
    ReceiptColumnConfigComponent,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatMenuModule,
    MatCheckboxModule,
    FormsModule,
    CommonModule,
    PageContainerComponent,
    BreadcrumbsComponent
  ]
})
export class ReceiptSearchComponent implements AfterViewInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private datasource = inject(ReceiptDatasourceService);
  private receiptApiClient = inject(CampaignsReceiptAdminApiClient);
  private dialog = inject(MatDialog);
  private readonly searchResultWindowLimit = 10000;

  readonly screen = inject(ScreenService);
  readonly appVersion = inject(AppVersionService);
  readonly grid = viewChild<ReceiptGridComponent>('grid');

  title = 'Receipt';

  // Search and filter properties
  searchText = '';
  selectedTags: string[] = [];
  private searchSubject = new Subject<string>();
  private subscriptions: Subscription[] = [];

  // Pagination properties
  currentPage = 1;
  pageSize = 25; // Match grid's default pageSize
  totalPages = 1;
  pageSizeOptions = [25, 50, 100]; // Available page size options

  // Search results
  searchResults: Receipt[] = [];
  isSearching = false;
  searchError = '';

  // Filtered results
  filteredReceipts: Receipt[] = [];

  // Total items from API
  totalItemsFromApi = 0;

  // Selected receipt index for keyboard navigation
  selectedReceiptIndex = -1;
  hoveredReceiptIndex = -1;

  // Keyboard navigation debouncing
  private isNavigating = false;
  private lastKeyPressTime = 0;
  private readonly keyDebounceMs = 50; // Minimum time between key presses

  constructor() {
    // Setup search debouncing
    const searchSub = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.performSearch();
    });
    this.subscriptions.push(searchSub);

    // Subscribe to datasource changes to update pagination
    const datasourceSub = this.datasource.pageAvailable.subscribe(() => {
      // When new data arrives, update pagination after a brief delay to ensure total is updated
      setTimeout(() => {
        this.updatePagination();
      }, 50);
    });
    this.subscriptions.push(datasourceSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  onSearchChange(event: any) {
    const searchTerm = event.target.value;
    this.datasource.searchText = searchTerm; // Store in datasource for highlighting
    this.currentPage = 1; // Reset to first page on search
    this.searchSubject.next(searchTerm);
  }

  toggleTag(tagValue: string) {
    const index = this.selectedTags.indexOf(tagValue);
    if (index > -1) {
      // Tag exists, remove it
      this.selectedTags = this.selectedTags.filter(t => t !== tagValue);
    } else {
      // Tag doesn't exist, add it
      this.selectedTags = [...this.selectedTags, tagValue];
    }

    // Trigger filter update
    this.currentPage = 1; // Reset to first page on filter change

    if (this.isSearchMode) {
      // In search mode
      if (this.selectedTags.length > 0) {
        this.performSearchWithTagFilter();
      } else {
        this.performSearch();
      }
    } else {
      // Not in search mode - pass tags to grid for server-side filtering
      this.loadGridWithTags();
    }
  }

  onTagsChange(event: any) {
    this.selectedTags = event.value;
    this.currentPage = 1; // Reset to first page on filter change

    if (this.isSearchMode) {
      // In search mode
      if (this.selectedTags.length > 0) {
        this.performSearchWithTagFilter();
      } else {
        this.performSearch();
      }
    } else {
      // Not in search mode - pass tags to grid for server-side filtering
      this.loadGridWithTags();
    }
  }

  private loadGridWithTags() {
    // Load grid data with tag filter
    const grid = this.grid();
    if (grid && grid.filter) {
      // Update filter with selected tags
      grid.filter = {
        ...grid.filter,
        tags: this.selectedTags.length > 0 ? this.selectedTags : undefined
      };
    }
    this.grid()?.loadPage(this.currentPage, this.pageSize);
    setTimeout(() => {
      this.updatePagination();
    }, 200);
  }

  clearSearch() {
    this.searchText = '';
    this.datasource.searchText = ''; // Clear highlighting
    this.currentPage = 1;
    this.searchResults = [];
    this.filteredReceipts = [];
    this.searchError = '';
    this.selectedTags = []; // Also clear tag filters
    this.totalItemsFromApi = 0;
    // Load default grid data
    this.grid()?.loadPage(1, this.pageSize);
    setTimeout(() => {
      this.updatePagination();
    }, 200);
  }

  clearTags() {
    this.selectedTags = [];
    this.currentPage = 1;
    if (this.isSearchMode) {
      this.performSearch();
    } else {
      // Clear tags and reload grid
      this.loadGridWithTags();
    }
  }

  private performSearchWithTagFilter() {
    const searchTerm = this.searchText?.trim();

    if (!searchTerm) {
      return;
    }

    // Fetch larger dataset for client-side tag filtering
    this.isSearching = true;
    this.searchError = '';

    const skip = 0;
    const maxItems = 500; // Fetch more for filtering

    this.receiptApiClient.searchReceiptsByText(searchTerm, skip, maxItems)
      .subscribe({
        next: (response) => {
          this.searchResults = response.receipts || [];
          this.applyFilters();
          this.isSearching = false;
        },
        error: (error) => {
          console.error('Search error:', error);
          this.searchError = error?.error?.message || 'Failed to search receipts. Please try again.';
          this.searchResults = [];
          this.filteredReceipts = [];
          this.datasource.receipts = [];
          this.datasource.total = 0;
          this.isSearching = false;
        }
      });
  }

  private performSearch() {
    const searchTerm = this.searchText?.trim();

    if (!searchTerm) {
      // If no search text, clear search results and load default grid data
      this.searchResults = [];
      this.filteredReceipts = [];
      this.searchError = '';
      this.totalItemsFromApi = 0;

      // Load grid data and update pagination based on datasource
      this.grid()?.loadPage(this.currentPage, this.pageSize);

      // Update pagination after datasource loads
      setTimeout(() => {
        this.updatePagination();
      }, 200);
      return;
    }

    // Perform search using the API with server-side pagination
    this.isSearching = true;
    this.searchError = '';

    // Calculate skip for server-side pagination
    let skip = (this.currentPage - 1) * this.pageSize;
    const maxSkip = Math.max(0, this.searchResultWindowLimit - this.pageSize);

    if (skip > maxSkip) {
      const alignedMaxSkip = Math.floor(maxSkip / this.pageSize) * this.pageSize;
      skip = alignedMaxSkip;
      this.currentPage = Math.max(1, Math.floor(skip / this.pageSize) + 1);
    }

    this.receiptApiClient.searchReceiptsByText(searchTerm, skip, this.pageSize)
      .subscribe({
        next: (response) => {
          this.searchResults = response.receipts || [];
          const rawTotal = response.total ?? 0;
          const cappedTotal = Math.min(rawTotal, this.searchResultWindowLimit);
          this.totalItemsFromApi = cappedTotal;

          // If no tag filters, use results directly
          if (!this.selectedTags || this.selectedTags.length === 0) {
            this.filteredReceipts = this.searchResults;
            this.datasource.receipts = this.searchResults;
            this.datasource.total = cappedTotal;
            this.datasource.pageAvailable.next(this.searchResults);
          } else {
            // Apply tag filters client-side (need to fetch more data)
            this.applyFilters();
          }

          // Update pagination
          this.updatePagination();

          this.isSearching = false;
        },
        error: (error) => {
          console.error('Search error:', error);
          this.searchError = error?.error?.message || 'Failed to search receipts. Please try again.';
          this.searchResults = [];
          this.filteredReceipts = [];
          this.datasource.receipts = [];
          this.datasource.total = 0;
          this.totalItemsFromApi = 0;
          this.updatePagination();
          this.isSearching = false;
        }
      });
  }

  private applyFilters() {
    let filtered = [...this.searchResults];

    // Apply tag filters
    if (this.selectedTags && this.selectedTags.length > 0) {
      filtered = filtered.filter(receipt => {
        if (!receipt.tags || receipt.tags.length === 0) {
          return false;
        }
        // Check if receipt has at least one of the selected tags (exact match)
        return this.selectedTags.some(selectedTag =>
          receipt.tags!.some(tag =>
            tag.trim() === selectedTag.trim()
          )
        );
      });
    }

    this.filteredReceipts = filtered;

    // Apply pagination to filtered results
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    const paginatedReceipts = this.filteredReceipts.slice(start, end);

    // Update datasource
    this.datasource.receipts = paginatedReceipts;
    this.datasource.total = this.filteredReceipts.length;

    // Update pagination
    this.updatePagination();

    // Update the grid
    this.datasource.pageAvailable.next(paginatedReceipts);
  }

  get availableTags(): { value: string; label: string; count?: number }[] {
    const predefinedTags = [
      'Incomplete',
      'NotRecognizable',
      'Duplicate',
      'VerifyDuplicate',
      'SuspectDuplicate',
      'SuspectDuplicateGroup_',
      'SuspectLocation',
      'NotEligible',
      'Eligible',
      'NotEnoughStamps',
      'ToManualVerify',
      'ToReprocess',
      'ManualAccept',
      'ManualReject',
      'ManualGiveStamps_',
      'ToCheckFraud',
      'ReprocessSuccess',
      'ReprocessFail',
      'NoLuckyDraw',
      'LuckyDrawReceived',
      'StampsReceived',
      'PointsReceived',
      'StaffReward',
      'StaffUserId',
      'ManualDuplicateAccept',
      'ManualDuplicateReject',
      'TimeConstraintNotValid',
      'CurrencyConstraintNotPass'
    ];

    const selectedTagSet = new Set(this.selectedTags?.map(tag => tag.trim()) ?? []);
    const hasSelectedTags = selectedTagSet.size > 0;

    // Count occurrences of each predefined tag in the current data
    const tagMap = new Map<string, number>();

    // Initialize all predefined tags with count 0
    predefinedTags.forEach(tag => {
      tagMap.set(tag, 0);
    });

    if (hasSelectedTags) {
      const receiptsToAnalyze = this.searchResults.length > 0 ? this.searchResults : this.datasource.receipts;

      receiptsToAnalyze.forEach(receipt => {
        if (receipt.tags && receipt.tags.length > 0) {
          receipt.tags.forEach(tag => {
            const normalizedTag = tag.trim();
            // Only count selected, predefined tags
            if (tagMap.has(normalizedTag) && selectedTagSet.has(normalizedTag)) {
              tagMap.set(normalizedTag, tagMap.get(normalizedTag)! + 1);
            }
          });
        }
      });
    }

    // Convert to array using the predefined order so dropdown order stays consistent
    return predefinedTags.map(tag => ({
      value: tag,
      label: tag,
      count: hasSelectedTags && selectedTagSet.has(tag) ? tagMap.get(tag) ?? 0 : undefined
    }));
  }

  get totalTagsCount(): number {
    return this.availableTags.reduce((sum, tag) => sum + (tag.count ?? 0), 0);
  }

  get totalItems(): number {
    // Use search results total if searching, otherwise use datasource total
    if (this.searchText?.trim()) {
      return this.totalItemsFromApi;
    }
    return this.datasource.total || 0;
  }

  private get cappedPaginationTotal(): number {
    return Math.min(this.totalItems, this.searchResultWindowLimit);
  }

  get currentPageItems(): string {
    const total = this.totalItems;
    if (total === 0) return '0';

    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, total);
    return `${start}-${end}`;
  }

  get isSearchMode(): boolean {
    return !!(this.searchText?.trim());
  }

  onReceiptSelected(receipt: any) {
    this.router.navigate(['/receipts', receipt.id]);
  }

  // Pagination methods
  private updatePagination() {
  const total = this.cappedPaginationTotal;
  this.totalPages = total > 0 ? Math.ceil(total / this.pageSize) : 0;

    if (this.totalPages > 0 && this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }

    if (this.totalPages === 0) {
      this.currentPage = 1;
    }
  }

  ngAfterViewInit() {
    // Check for query params to handle search text from navigation
    this.route.queryParams.subscribe(params => {
      if (params['searchText']) {
        this.searchText = params['searchText'];
        this.datasource.searchText = params['searchText'];
        this.currentPage = 1;
        // Trigger search after a short delay to ensure view is ready
        setTimeout(() => {
          this.performSearch();
        }, 100);
      } else if (params['suspectDuplicateGroup']) {
        // Handle legacy suspectDuplicateGroup param if needed
        // For now, load initial data
        this.grid()?.loadPage(this.currentPage, this.pageSize);
        setTimeout(() => {
          this.updatePagination();
        }, 200);
      } else {
        // Load initial data
        this.grid()?.loadPage(this.currentPage, this.pageSize);
        setTimeout(() => {
          this.updatePagination();
        }, 200);
      }
    });
  }

  getShowingText(): string {
    if (this.totalItems === 0) return '0-0';

    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalItems);
    return `${start}-${end}`;
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      if (this.isSearchMode) {
        this.performSearch(); // Search with API
      } else {
        this.grid()?.loadPage(this.currentPage, this.pageSize); // Load grid data
        setTimeout(() => {
          this.updatePagination();
        }, 200);
      }
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      if (this.isSearchMode) {
        this.performSearch(); // Search with API
      } else {
        this.grid()?.loadPage(this.currentPage, this.pageSize); // Load grid data
        setTimeout(() => {
          this.updatePagination();
        }, 200);
      }
    }
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      if (this.isSearchMode) {
        this.performSearch(); // Search with API
      } else {
        this.grid()?.loadPage(this.currentPage, this.pageSize); // Load grid data
        setTimeout(() => {
          this.updatePagination();
        }, 200);
      }
    }
  }

  getVisiblePages(): (number | string)[] {
    const visiblePages: (number | string)[] = [];
    const totalPages = this.totalPages;
    const current = this.currentPage;

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        visiblePages.push(i);
      }
    } else {
      // Always show first page
      visiblePages.push(1);

      if (current <= 4) {
        // Show pages 2, 3, 4, 5, ..., last
        for (let i = 2; i <= 5; i++) {
          visiblePages.push(i);
        }
        visiblePages.push('...');
        visiblePages.push(totalPages);
      } else if (current >= totalPages - 3) {
        // Show 1, ..., last-4, last-3, last-2, last-1, last
        visiblePages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          visiblePages.push(i);
        }
      } else {
        // Show 1, ..., current-1, current, current+1, ..., last
        visiblePages.push('...');
        for (let i = current - 1; i <= current + 1; i++) {
          visiblePages.push(i);
        }
        visiblePages.push('...');
        visiblePages.push(totalPages);
      }
    }

    return visiblePages;
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1; // Reset to first page when page size changes

    if (this.isSearchMode) {
      this.performSearch(); // Re-search with new page size
    } else {
      this.grid()?.loadPage(this.currentPage, this.pageSize); // Reload grid with new page size
      setTimeout(() => {
        this.updatePagination();
      }, 200);
    }
  }

  // Handle row click to select receipt
  onReceiptRowClick(index: number) {
    this.selectedReceiptIndex = index;
    this.hoveredReceiptIndex = index;
    this.showReceiptDetails(index);
  }

  onReceiptCtrlClick(receipt: Receipt) {
    if (!receipt?.id) {
      return;
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/receipts', receipt.id])
    );
    window.open(url, '_blank');
  }

  // Handle row hover
  onReceiptRowHover(index: number) {
    this.hoveredReceiptIndex = index;
  }

  // Show receipt details in side panel
  private showReceiptDetails(index: number) {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      const gridComponent = this.grid();
      if (gridComponent) {
        const maxIndex = gridComponent.getNavigableRowCount();
        if (index >= 0 && index < maxIndex) {
          gridComponent.selectReceiptByIndex(index);
        }
      }
    });
  }

  // Keyboard shortcuts handler
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Don't handle shortcuts if user is typing in an input/textarea
    const target = event.target as HTMLElement;
    const tagName = target.tagName.toLowerCase();
    const isInputField = tagName === 'input' || tagName === 'textarea' || target.isContentEditable;

    // Allow Esc to work even in input fields (to blur/close)
    if (event.key === 'Escape') {
      if (this.dialog.openDialogs.length > 0) {
        return;
      }
      event.preventDefault();
      this.handleEscape();
      return;
    }

    // Don't handle other shortcuts if user is typing
    if (isInputField) {
      return;
    }

    const receipts = this.datasource.receipts;
    if (!receipts || receipts.length === 0) {
      return;
    }

    // Debounce navigation keys to prevent rapid firing
    const now = Date.now();
    const isNavigationKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key);

    if (isNavigationKey && now - this.lastKeyPressTime < this.keyDebounceMs) {
      event.preventDefault();
      return;
    }

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.lastKeyPressTime = now;
        this.navigateUp();
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.lastKeyPressTime = now;
        this.navigateDown();
        break;

      case 'ArrowLeft':
        event.preventDefault();
        this.lastKeyPressTime = now;
        this.navigateLeftTab();
        break;

      case 'ArrowRight':
        event.preventDefault();
        this.lastKeyPressTime = now;
        this.navigateRightTab();
        break;

      case 'Enter':
        event.preventDefault();
        if (event.ctrlKey || event.metaKey) {
          this.openReceiptInNewTab();
        } else {
          this.openReceiptDetails();
        }
        break;

      case 'c':
      case 'C':
        if (!event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          this.closeSidePanel();
        }
        break;

      case 'x':
      case 'X':
        if (!event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          this.toggleExpandCollapseAll();
        }
        break;
    }
  }

  // Keyboard shortcut methods
  private navigateUp() {
    // Prevent multiple simultaneous navigations
    if (this.isNavigating) {
      return;
    }

    const gridComponent = this.grid();
    if (!gridComponent) return;

    const maxIndex = gridComponent.getNavigableRowCount();
    if (maxIndex === 0) return;

    // Determine starting point
    let currentIndex = this.selectedReceiptIndex;

    // If nothing is selected but mouse is hovering, start from hovered position
    if (currentIndex === -1 && this.hoveredReceiptIndex !== -1) {
      currentIndex = this.hoveredReceiptIndex;
      this.selectedReceiptIndex = currentIndex;
    }

    // If still no selection, start from first item
    if (currentIndex === -1) {
      this.selectedReceiptIndex = 0;
      this.hoveredReceiptIndex = 0;
      this.showReceiptDetails(this.selectedReceiptIndex);
      return;
    }

    if (currentIndex > 0) {
      this.selectedReceiptIndex = currentIndex - 1;
      this.hoveredReceiptIndex = this.selectedReceiptIndex;
      this.showReceiptDetails(this.selectedReceiptIndex);
    } else if (currentIndex === 0 && this.currentPage > 1) {
      // Go to previous page and select last item
      this.isNavigating = true;
      this.previousPage();

      // Wait for data to load by subscribing to pageAvailable
      const subscription = this.datasource.pageAvailable.subscribe(() => {
        subscription.unsubscribe();
        requestAnimationFrame(() => {
          const grid = this.grid();
          if (grid) {
            const count = grid.getNavigableRowCount();
            if (count > 0) {
              this.selectedReceiptIndex = count - 1;
              this.hoveredReceiptIndex = this.selectedReceiptIndex;
              this.showReceiptDetails(this.selectedReceiptIndex);
            }
          }
          this.isNavigating = false;
        });
      });

      // Fallback timeout in case subscription doesn't fire
      setTimeout(() => {
        if (this.isNavigating) {
          subscription.unsubscribe();
          this.isNavigating = false;
        }
      }, 1000);
    }
  }

  private navigateDown() {
    // Prevent multiple simultaneous navigations
    if (this.isNavigating) {
      return;
    }

    const gridComponent = this.grid();
    if (!gridComponent) return;

    const maxIndex = gridComponent.getNavigableRowCount();
    if (maxIndex === 0) return;

    // Determine starting point
    let currentIndex = this.selectedReceiptIndex;

    // If nothing is selected but mouse is hovering, start from hovered position
    if (currentIndex === -1 && this.hoveredReceiptIndex !== -1) {
      currentIndex = this.hoveredReceiptIndex;
      this.selectedReceiptIndex = currentIndex;
    }

    // If still no selection, start from first item
    if (currentIndex === -1) {
      this.selectedReceiptIndex = 0;
      this.hoveredReceiptIndex = 0;
      this.showReceiptDetails(this.selectedReceiptIndex);
      return;
    }

    if (currentIndex >= 0 && currentIndex < maxIndex - 1) {
      this.selectedReceiptIndex = currentIndex + 1;
      this.hoveredReceiptIndex = this.selectedReceiptIndex;
      this.showReceiptDetails(this.selectedReceiptIndex);
    } else if (currentIndex === maxIndex - 1 && this.currentPage < this.totalPages) {
      // Go to next page and select first item
      this.isNavigating = true;
      this.nextPage();

      // Wait for data to load by subscribing to pageAvailable
      const subscription = this.datasource.pageAvailable.subscribe(() => {
        subscription.unsubscribe();
        requestAnimationFrame(() => {
          const grid = this.grid();
          if (grid && grid.getNavigableRowCount() > 0) {
            this.selectedReceiptIndex = 0;
            this.hoveredReceiptIndex = 0;
            this.showReceiptDetails(this.selectedReceiptIndex);
          }
          this.isNavigating = false;
        });
      });

      // Fallback timeout in case subscription doesn't fire
      setTimeout(() => {
        if (this.isNavigating) {
          subscription.unsubscribe();
          this.isNavigating = false;
        }
      }, 1000);
    }
  }

  private navigateLeftTab() {
    // Navigate to previous campaign tab (delegated to detail panel)
    const gridComponent = this.grid();
    if (gridComponent && gridComponent.detailPanel) {
    }
  }

  private navigateRightTab() {
    // Navigate to next campaign tab (delegated to detail panel)
    const gridComponent = this.grid();
    if (gridComponent && gridComponent.detailPanel) {
    }
  }

  private openReceiptDetails() {
    const receipts = this.datasource.receipts;
    if (this.selectedReceiptIndex >= 0 && this.selectedReceiptIndex < receipts.length) {
      const receipt = receipts[this.selectedReceiptIndex];
      this.router.navigate(['/receipts', receipt.id]);
    }
  }

  private openReceiptInNewTab() {
    const receipts = this.datasource.receipts;
    if (this.selectedReceiptIndex >= 0 && this.selectedReceiptIndex < receipts.length) {
      const receipt = receipts[this.selectedReceiptIndex];
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/receipts', receipt.id])
      );
      window.open(url, '_blank');
    }
  }

  private handleEscape() {
    // Close side panel or blur input fields
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      activeElement.blur();
    }
    this.closeSidePanel();
  }

  private closeSidePanel() {
    const gridComponent = this.grid();
    if (gridComponent && gridComponent.detailPanel) {
      const panelDrawer = gridComponent.detailPanel()?.drawer;
      if (panelDrawer) {
        panelDrawer()?.close();
      }
    }
  }

  private toggleExpandCollapseAll() {
    const gridComponent = this.grid();
    if (!gridComponent) {
      return;
    }

    if (gridComponent.areAllGroupsExpanded()) {
      gridComponent.collapseAllGroups();
    } else {
      gridComponent.expandAllGroups();
    }
  }

}
